/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { identity } from 'ramda'
import { useKeydown } from '@btime/react-hooks'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { SmallText } from '../presentation/Text'
import { VerticalLine } from '../shapes/Line'
import { WrapLoading } from '../helpers/Loading'
import FlexGrid, { FlexCol } from './FlexGrid'
import Icon from '../shapes/Icon'
import Link from '../actions/Link'

export const FooterModalLink = ({ icon, label, onClick, color, loading, tourStep, t = identity, to }) => {
  const handleClick = () => {
    onClick()
  }

  return (
    <FlexCol flex={0}>
      <Link block onClick={handleClick} to={to}>
        <FlexGrid gutter={0.2} direction="column">
          <Icon name={icon} size="1.4rem" color={color} />
          <SmallText color={color} size="xsmall" align="center" noWrap>
            {t(label)}
          </SmallText>
        </FlexGrid>
      </Link>
    </FlexCol>
  )
}

const FooterModalPanel = ({ loading, children, bgColor, onClose, ...props }) => {
  const { t } = useTranslation('components')
  const wrapperRef = React.useRef()
  useKeydown(wrapperRef, { keys: ['Escape'], action: onClose })

  const style = css`
    min-width: 64rem;
    max-width: 100%;
    max-height: 100%;
    background: ${bgColor || COLORS.white};
    box-shadow: ${COLORS.lightShadow1};
    border-radius: 5px 5px 0 0;
    padding: 1rem;
  `

  const color = bgColor ? COLORS.white : COLORS.dark3

  return (
    <div className="footerModalPanel" css={style} ref={wrapperRef}>
      <WrapLoading loading={loading} bgColor={COLORS.dark2 + '70'} color={COLORS.white}>
        <FlexGrid gutter={0.5} align="center" justify="space-between">
          {children}

          <FlexCol alignSelf="stretch">
            <VerticalLine thin lineColor={color + '60'} fullHeight />
          </FlexCol>
          <FooterModalLink icon="BsX" color={color} onClick={onClose} label={t('cancel')} />
        </FlexGrid>
      </WrapLoading>
    </div>
  )
}

const FooterModalWrapper = ({ onClose, children, ...props }) => {
  const style = css`
    bottom: 0;
    left: 50%;
    right: 50%;
    position: fixed;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  `

  return (
    <div className="footerModal" css={style}>
      {children}
    </div>
  )
}

const FooterModal = ({ open, onClose, children, ...props }) => {
  if (!open) return false

  return (
    <FooterModalWrapper>
      <FooterModalPanel onClose={onClose} {...props}>
        {children}
      </FooterModalPanel>
    </FooterModalWrapper>
  )
}

export default FooterModal
